import { FC, useEffect, useState } from "react";
import { dispatch } from "../../dispatch/dispatch";
import { actions } from "../../dispatch";
import { t } from "../../i18n/ar";
import { SkuLink } from "../SkuLink";
import StoreScoreChip from "../../../common/StoreScoreChip";

// ─── 🚨 Hand-over alerts (user 2026-10-21) ──────────────────────────────────
// Parcels we handed to Jumia / Marjane that were cancelled right after (backend controllers/handover-alerts.js).
// Red = Marjane cancelled the order BEFORE / at its own scan ("Annulation droppoff": parcel taken, order
// cancelled, nothing registered to come back). Each card says whether the parcel is back in the warehouse.
interface IAlertItem { title: string; image: string | null; sku: string | null; ean: string | null; qty: number; price: number | null }
export interface IHandoverAlert {
    id: string; platform: "jumia" | "marjan"; store: string; orderRef: string; orderId: string | null; address?: string | null;
    kind: "cancel_before_scan" | "cancel_after_scan" | "cancel_no_scan" | "jumia_returned_fast" | "jumia_failed_fast";
    handedAt: string | null; printedAt: string | null; scannedAt: string | null; cancelledAt: string | null; gapMin: number | null;
    reason: string | null; city: string | null; value: number; items: IAlertItem[]; tracking?: string | null;
    back: { state: string; receivedAt: string | null } | null;
}
export interface IHandoverAlertsResponse { alerts: IHandoverAlert[]; counts: { total: number; notBack: number; critical: number; notBackValue: number } }

const when = (iso?: string | null) => iso
    ? new Date(iso).toLocaleString("ar-MA", { timeZone: "Africa/Casablanca", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit", hour12: false })
    : "—";
const gapText = (min: number | null) => {
    if (min == null) return "";
    const a = Math.abs(min);
    const txt = a < 60 ? `${a} دقيقة` : a < 48 * 60 ? `${Math.round(a / 60)} ساعة` : `${Math.round(a / 1440)} يوم`;
    return min <= 0 ? `قبل الاستلام بـ ${txt}` : `بعد الاستلام بـ ${txt}`;
};
const kindLabel = (k: IHandoverAlert["kind"]) =>
    k === "cancel_before_scan" ? t.alertCritical : k === "cancel_after_scan" ? t.alertAfterScan : k === "cancel_no_scan" ? t.alertNoScan : t.alertJumiaFast;

export const Alerts: FC<{ setShowSpinner: (v: boolean) => void }> = ({ setShowSpinner }) => {
    const [data, setData] = useState<IHandoverAlertsResponse | null>(null);
    const [error, setError] = useState("");
    const [onlyOpen, setOnlyOpen] = useState(true);

    useEffect(() => {
        (async () => {
            setShowSpinner(true);
            const r = await dispatch(actions.getHandoverAlerts, {}, { days: 60 });
            setShowSpinner(false);
            if (!r || "error" in r) return setError(r?.error?.message || "error");
            setData(r);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const list = (data?.alerts || []).filter((a) => !onlyOpen || !a.back?.receivedAt);

    return (
        <div className="mt-4">
            <h1 className="flex items-center gap-2 text-3xl font-black text-slate-800"><span aria-hidden>🚨</span> {t.alertTitle}</h1>
            <p className="mt-1 text-lg font-bold text-slate-500">{t.alertHint}</p>
            {error && <p className="mt-4 rounded-2xl bg-red-100 px-4 py-6 text-center text-2xl font-black text-red-700">❌ {error}</p>}

            {data && (
                <>
                    <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                        <div className="rounded-2xl bg-red-600 px-4 py-3 text-center text-2xl font-black text-white">{t.alertSummary(data.counts.notBack, data.counts.notBackValue)}</div>
                        <div className="rounded-2xl bg-red-100 px-4 py-3 text-center text-xl font-black text-red-800">⛔ {t.alertCriticalCount(data.counts.critical)}</div>
                    </div>
                    <div className="mt-3 flex overflow-hidden rounded-xl border-2 border-slate-300">
                        {[true, false].map((v) => (
                            <button key={String(v)} onClick={() => setOnlyOpen(v)}
                                className={`flex-1 px-3 py-2 text-lg font-black ${onlyOpen === v ? "bg-slate-800 text-white" : "bg-white text-slate-500"}`}>
                                {v ? t.alertFilterOpen : t.alertFilterAll}
                            </button>
                        ))}
                    </div>

                    {list.length === 0 && <p className="mt-4 rounded-2xl bg-emerald-100 px-4 py-6 text-center text-3xl font-black text-emerald-700">{t.alertNone}</p>}

                    <div className="mt-4 space-y-4">
                        {list.map((a) => {
                            // RED = cancelled AFTER we handed it over (it really left); yellow = cancelled before the handover (user 2026-10-23)
                            const critical = a.kind === "cancel_after_scan" || a.kind === "jumia_returned_fast" || a.kind === "jumia_failed_fast";
                            const isBack = !!a.back?.receivedAt;
                            return (
                                <div key={a.id} className={`overflow-hidden rounded-3xl border-4 bg-white shadow ${isBack ? "border-emerald-500" : critical ? "border-red-600" : "border-amber-400"}`}>
                                    <div className={`flex flex-wrap items-center gap-3 px-4 py-2 text-white ${a.platform === "marjan" ? "bg-[#00847f]" : "bg-[#f68b1e]"}`}>
                                        <span className="text-xl font-black">{a.platform === "marjan" ? "🛒 مرجان" : "🟠 جوميا"}</span>
                                        <span className="ltr font-mono text-2xl font-black">{a.orderRef}</span>
                                        <span className="ltr text-sm font-bold text-white/80">{a.store}{a.platform === "jumia" && <StoreScoreChip store={a.store} />}</span>
                                        <span className={`ms-auto rounded-lg px-2 py-0.5 text-base font-black ${isBack ? "bg-emerald-600" : "bg-red-700"}`}>
                                            {isBack ? `✅ ${t.alertBack}` : `❌ ${t.alertNotBack}`}
                                        </span>
                                    </div>
                                    <div className={`px-4 py-2 text-xl font-black ${critical ? "bg-red-50 text-red-800" : "bg-amber-50 text-amber-900"}`}>
                                        {critical ? "⛔" : "⚠️"} {kindLabel(a.kind)}{a.gapMin != null && a.platform === "marjan" ? ` · ${gapText(a.gapMin)}` : ""}
                                    </div>
                                    {a.items.map((it, i) => (
                                        <div key={i} className="flex items-center gap-4 px-4 pt-3">
                                            {it.image ? <img src={it.image} alt="" className="h-24 w-24 rounded-xl border border-slate-200 bg-white object-contain" /> : <div className="h-24 w-24 rounded-xl bg-slate-100" />}
                                            <div className="min-w-0 flex-1">
                                                <span className="ltr inline-block rounded-xl bg-slate-100 px-3 py-1 text-2xl font-black text-slate-900">×{it.qty || 1}</span>
                                                <p className="ltr mt-1 text-start text-lg font-bold text-slate-700">{it.title}</p>
                                                {it.sku && <p className="ltr text-start font-mono text-base font-black text-slate-500"><SkuLink sku={it.sku} /></p>}
                                                {it.ean && <p className="ltr text-start font-mono text-sm text-slate-400">EAN {it.ean}</p>}
                                            </div>
                                        </div>
                                    ))}
                                    <div className="grid grid-cols-1 gap-1 p-4 text-lg font-bold text-slate-700 sm:grid-cols-2">
                                        {a.printedAt && <p>🖨️ {t.alertPrinted}: <span className="font-black">{when(a.printedAt)}</span></p>}
                                        <p>🤝 {t.alertHanded}: <span className="font-black">{when(a.scannedAt || a.handedAt)}</span></p>
                                        <p className={critical ? "text-red-700" : ""}>✖️ {t.alertCancelled}: <span className="font-black">{when(a.cancelledAt)}</span></p>
                                        {(a.city || a.address) && <p className="sm:col-span-2">📍 المدينة ديال الكليان: <span className="ltr inline-block font-black">{a.city || "?"}</span>{a.address ? <span className="ltr block text-sm font-bold text-slate-500">{a.address}</span> : null}</p>}
                                        <p>💰 {t.alertValue}: <span className="ltr inline-block font-black">{a.value} MAD</span></p>
                                        {a.reason && <p className="sm:col-span-2">📝 {t.alertReason}: <span className="ltr inline-block font-black">{a.reason}</span></p>}
                                        {a.tracking && <p className="ltr text-start font-mono text-sm text-slate-400 sm:col-span-2">{a.tracking}</p>}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
};
